import { getPreferredBookmaker } from "../components/lib/oddsGames";
import { formatOdds, formatPoint, formatTotalPoint } from "./odds";
import {
  findDrawOutcome,
  getDrawDisplayLabel,
  isDrawOutcomeName,
  resolveH2hSides,
} from "./h2hOutcomes";

const EMPTY_CELL = { display: "-", isPlacedBet: false };

function findOutcome(market, name) {
  if (!market?.outcomes || !name) return null;
  return market.outcomes.find((outcome) => outcome.name === name) || null;
}

function findOutcomeMarket(bookmaker, key) {
  return bookmaker?.markets?.find((market) => market.key === key) || null;
}

/** Whether a market cell matches the ticket the user placed. */
export function isPlacedBetOutcome(bet, marketKey, outcome) {
  if (!bet || !outcome || !marketKey) return false;

  const oddType = String(bet.oddType || "").trim();
  if (oddType !== marketKey) return false;

  if (marketKey === "totals") {
    const selection = String(bet.pointType || bet.teamName || "").trim();
    return selection === outcome.name;
  }

  if (marketKey === "h2h") {
    if (isDrawOutcomeName(bet.teamName)) {
      return isDrawOutcomeName(outcome.name);
    }
    return String(bet.teamName || "").trim() === String(outcome.name || "").trim();
  }

  if (String(bet.teamName || "").trim() !== String(outcome.name || "").trim()) {
    return false;
  }

  if (marketKey === "spreads") {
    const betPoint = Number(bet.point);
    const outcomePoint = Number(outcome.point);
    if (Number.isFinite(betPoint) && Number.isFinite(outcomePoint)) {
      return Math.abs(betPoint - outcomePoint) < 0.001;
    }
  }

  return true;
}

function cellFromOutcome(outcome, bet, marketKey) {
  if (!outcome) return { ...EMPTY_CELL };

  const isPlacedBet = isPlacedBetOutcome(bet, marketKey, outcome);

  if (marketKey === "h2h") {
    return {
      display: formatOdds(outcome.price),
      price: outcome.price,
      label: isDrawOutcomeName(outcome.name)
        ? getDrawDisplayLabel(outcome.name)
        : undefined,
      isPlacedBet,
    };
  }

  if (marketKey === "totals") {
    return {
      point: outcome.point ?? null,
      pointDisplay: formatTotalPoint(outcome.point, outcome.name),
      label: outcome.name,
      display: formatOdds(outcome.price),
      price: outcome.price,
      isPlacedBet,
    };
  }

  return {
    point: outcome.point ?? null,
    pointDisplay: formatPoint(outcome.point),
    display: formatOdds(outcome.price),
    price: outcome.price,
    isPlacedBet,
  };
}

function fallbackCell(bet, marketKey, teamSide) {
  if (!bet) return { ...EMPTY_CELL };

  const oddType = String(bet.oddType || "").trim();
  if (oddType !== marketKey) return { ...EMPTY_CELL };

  const totalsName = teamSide === "home" ? "Over" : "Under";

  if (marketKey === "totals") {
    const selection = String(bet.pointType || bet.teamName || "").trim();
    if (selection !== totalsName) return { ...EMPTY_CELL };
    return {
      point: bet.point ?? null,
      pointDisplay: formatTotalPoint(bet.point, selection),
      label: selection,
      display: formatOdds(bet.price),
      price: bet.price,
      isPlacedBet: true,
    };
  }

  const teamName = teamSide === "home" ? bet.homeTeam : bet.awayTeam;
  if (String(bet.teamName || "").trim() !== String(teamName || "").trim()) {
    return { ...EMPTY_CELL };
  }

  if (marketKey === "h2h") {
    if (isDrawOutcomeName(bet.teamName)) {
      return { ...EMPTY_CELL };
    }
    return {
      display: formatOdds(bet.price),
      price: bet.price,
      isPlacedBet: true,
    };
  }

  return {
    point: bet.point ?? null,
    pointDisplay: formatPoint(bet.point),
    display: formatOdds(bet.price),
    price: bet.price,
    isPlacedBet: true,
  };
}

function buildDrawRow(spreads, h2h, totals, bet) {
  const drawOutcome = findDrawOutcome(h2h).outcome;

  return {
    spreads: { ...EMPTY_CELL },
    h2h: drawOutcome
      ? {
          ...cellFromOutcome(drawOutcome, bet, "h2h"),
          label: getDrawDisplayLabel(drawOutcome.name),
        }
      : isDrawOutcomeName(bet?.teamName)
        ? {
            display: formatOdds(bet.price),
            price: bet.price,
            label: getDrawDisplayLabel(bet.teamName),
            isPlacedBet: true,
          }
        : { ...EMPTY_CELL },
    totals: { ...EMPTY_CELL },
  };
}

function buildRow(homeTeam, awayTeam, spreads, h2h, totals, bet, teamSide) {
  const teamName = teamSide === "home" ? homeTeam : awayTeam;
  const totalsName = teamSide === "home" ? "Over" : "Under";

  const spreadOutcome = findOutcome(spreads, teamName);
  const h2hOutcome = findOutcome(h2h, teamName);
  const totalsOutcome = findOutcome(totals, totalsName);

  return {
    spreads: spreadOutcome
      ? cellFromOutcome(spreadOutcome, bet, "spreads")
      : fallbackCell(bet, "spreads", teamSide),
    h2h: h2hOutcome
      ? cellFromOutcome(h2hOutcome, bet, "h2h")
      : fallbackCell(bet, "h2h", teamSide),
    totals: totalsOutcome
      ? cellFromOutcome(totalsOutcome, bet, "totals")
      : fallbackCell(bet, "totals", teamSide),
  };
}

export function getMatchPhase(matchTime, game) {
  if (game?.completed === true) return "ended";

  if (!matchTime) return "upcoming";

  const now = Date.now();
  const matchMs = new Date(matchTime).getTime();
  if (Number.isNaN(matchMs)) return "upcoming";

  const diffMinutes = (matchMs - now) / (1000 * 60);
  if (diffMinutes > 0) return "upcoming";
  if (diffMinutes > -90) return "live";
  return "ended";
}

/** Read-only spread / money / total grid for active-bet row expand. */
export function getActiveBetMatchView(game, bet) {
  const homeTeam = game?.home_team || bet?.homeTeam || "";
  const awayTeam = game?.away_team || bet?.awayTeam || "";
  const bookmaker = getPreferredBookmaker(game?.bookmakers || []);
  const spreads = findOutcomeMarket(bookmaker, "spreads");
  const h2h = findOutcomeMarket(bookmaker, "h2h");
  const totals = findOutcomeMarket(bookmaker, "totals");

  const h2hSides = resolveH2hSides(h2h, homeTeam, awayTeam);

  const rows = [
    buildRow(homeTeam, awayTeam, spreads, h2h, totals, bet, "home"),
  ];

  if (h2hSides.isThreeWay) {
    rows.push(buildDrawRow(spreads, h2h, totals, bet));
  }

  rows.push(buildRow(homeTeam, awayTeam, spreads, h2h, totals, bet, "away"));

  return {
    sportTitle: game?.sport_title || bet?.sport_title || bet?.title || "",
    homeTeam,
    awayTeam,
    commenceTime: game?.commence_time || bet?.gameTime || "",
    phase: getMatchPhase(game?.commence_time || bet?.gameTime, game),
    rows,
    hasLiveOdds: Boolean(bookmaker),
    placedSelection: {
      teamName: bet?.teamName,
      oddType: bet?.oddType,
      point: bet?.point,
      price: bet?.price,
      pointType: bet?.pointType,
    },
  };
}
